import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    SchoolTime,
    TimeSlot,
    MasterSchedule,
    ClassSchedule,
    SchedulePeriod,
    ScheduleApproval
} from '../types/schedule';

// School Time Management
export const getSchoolTimes = async (): Promise<SchoolTime[]> => {
    try {
        const schoolTimesRef = collection(db, 'schoolTimes');
        const q = query(schoolTimesRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as SchoolTime[];
    } catch (error) {
        console.error('Error fetching school times:', error);
        return [];
    }
};

export const addSchoolTime = async (schoolTimeData: Omit<SchoolTime, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        const schoolTimesRef = collection(db, 'schoolTimes');
        const docRef = await addDoc(schoolTimesRef, {
            ...schoolTimeData,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding school time:', error);
        throw error;
    }
};

export const updateSchoolTime = async (id: string, schoolTimeData: Partial<SchoolTime>): Promise<void> => {
    try {
        const schoolTimeRef = doc(db, 'schoolTimes', id);
        await updateDoc(schoolTimeRef, {
            ...schoolTimeData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating school time:', error);
        throw error;
    }
};

export const deleteSchoolTime = async (id: string): Promise<void> => {
    try {
        const schoolTimeRef = doc(db, 'schoolTimes', id);
        await deleteDoc(schoolTimeRef);
    } catch (error) {
        console.error('Error deleting school time:', error);
        throw error;
    }
};

// Master Schedule Management
export const getMasterSchedules = async (): Promise<MasterSchedule[]> => {
    try {
        const schedulesRef = collection(db, 'masterSchedules');
        const q = query(schedulesRef, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            approvedAt: doc.data().approvedAt?.toDate(),
            effectiveDate: doc.data().effectiveDate?.toDate(),
        })) as MasterSchedule[];
    } catch (error) {
        console.error('Error fetching master schedules:', error);
        return [];
    }
};

export const getMasterScheduleById = async (id: string): Promise<MasterSchedule | null> => {
    try {
        const scheduleRef = doc(db, 'masterSchedules', id);
        const scheduleDoc = await getDoc(scheduleRef);

        if (scheduleDoc.exists()) {
            const data = scheduleDoc.data();
            return {
                id: scheduleDoc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                approvedAt: data.approvedAt?.toDate(),
                effectiveDate: data.effectiveDate?.toDate(),
            } as MasterSchedule;
        }
        return null;
    } catch (error) {
        console.error('Error fetching master schedule:', error);
        return null;
    }
};

export const addMasterSchedule = async (scheduleData: Omit<MasterSchedule, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        const schedulesRef = collection(db, 'masterSchedules');
        const docRef = await addDoc(schedulesRef, {
            ...scheduleData,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding master schedule:', error);
        throw error;
    }
};

export const updateMasterSchedule = async (id: string, scheduleData: Partial<MasterSchedule>): Promise<void> => {
    try {
        const scheduleRef = doc(db, 'masterSchedules', id);
        await updateDoc(scheduleRef, {
            ...scheduleData,
            updatedAt: new Date(),
        });
    } catch (error) {
        console.error('Error updating master schedule:', error);
        throw error;
    }
};

export const deleteMasterSchedule = async (id: string): Promise<void> => {
    try {
        const scheduleRef = doc(db, 'masterSchedules', id);
        await deleteDoc(scheduleRef);
    } catch (error) {
        console.error('Error deleting master schedule:', error);
        throw error;
    }
};

// Schedule Approval Management
export const submitScheduleForApproval = async (scheduleId: string): Promise<void> => {
    try {
        await updateMasterSchedule(scheduleId, {
            status: 'pending_approval'
        });
    } catch (error) {
        console.error('Error submitting schedule for approval:', error);
        throw error;
    }
};

export const approveSchedule = async (scheduleId: string, approvedBy: string, effectiveDate: Date): Promise<void> => {
    try {
        // First, deactivate any existing active schedules
        const activeSchedules = await getMasterSchedules();
        const activeSchedule = activeSchedules.find(s => s.status === 'active');

        if (activeSchedule) {
            await updateMasterSchedule(activeSchedule.id, {
                status: 'approved' // Change from active to approved
            });
        }

        // Approve the new schedule
        await updateMasterSchedule(scheduleId, {
            status: 'approved',
            approvedBy,
            approvedAt: new Date(),
            effectiveDate
        });
    } catch (error) {
        console.error('Error approving schedule:', error);
        throw error;
    }
};

export const activateSchedule = async (scheduleId: string): Promise<void> => {
    try {
        await updateMasterSchedule(scheduleId, {
            status: 'active'
        });
    } catch (error) {
        console.error('Error activating schedule:', error);
        throw error;
    }
};

export const rejectSchedule = async (scheduleId: string, rejectedBy: string, rejectionReason: string): Promise<void> => {
    try {
        await updateMasterSchedule(scheduleId, {
            status: 'rejected',
            approvedBy: rejectedBy,
            approvedAt: new Date(),
            rejectionReason
        });
    } catch (error) {
        console.error('Error rejecting schedule:', error);
        throw error;
    }
};

// Helper functions
export const getActiveSchedule = async (): Promise<MasterSchedule | null> => {
    try {
        const schedulesRef = collection(db, 'masterSchedules');
        const q = query(schedulesRef, where('status', '==', 'active'));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            approvedAt: data.approvedAt?.toDate(),
            effectiveDate: data.effectiveDate?.toDate(),
        } as MasterSchedule;
    } catch (error) {
        console.error('Error fetching active schedule:', error);
        return null;
    }
};

export const getPendingApprovalSchedules = async (): Promise<MasterSchedule[]> => {
    try {
        const schedulesRef = collection(db, 'masterSchedules');
        const q = query(schedulesRef, where('status', '==', 'pending_approval'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            approvedAt: doc.data().approvedAt?.toDate(),
            effectiveDate: doc.data().effectiveDate?.toDate(),
        })) as MasterSchedule[];
    } catch (error) {
        console.error('Error fetching pending approval schedules:', error);
        return [];
    }
};
